import type { ViewMode } from '@/types';
import type { ItemType } from '@/store/appStore';

export type { ItemType };
export type DeviationGroupBy = 'person' | 'task' | 'space' | 'list' | null;
export type DeviationFilter = 'all' | 'over' | 'under' | 'on-time';

export interface UrlState {
  viewMode: ViewMode;
  path: string | null; // Unified path: "my-space" or "my-space/my-folder" or "my-space/my-folder/my-list"
  taskId: string | null;
  itemType: ItemType | null;
  itemId: string | null;
  // Deviation view parameters
  deviationGroupBy?: DeviationGroupBy;
  deviationFilter?: DeviationFilter;
  deviationAssignee?: string | null;
}

const VIEW_MODES: ViewMode[] = ['list', 'board', 'gantt', 'sprint', 'calendar', 'inbox', 'team', 'timesheet', 'projects', 'deviations', 'report'];

// View modes that don't support path filters
const viewModesWithoutPathFilters: ViewMode[] = ['inbox', 'team', 'timesheet', 'projects', 'deviations', 'report'];

export function parseUrl(): UrlState {
  const pathname = window.location.pathname;
  const searchParams = new URLSearchParams(window.location.search);
  const pathParts = pathname.split('/').filter(Boolean);

  let viewMode: ViewMode = 'list';
  let path: string | null = null;
  let taskId: string | null = null;
  let itemType: ItemType | null = null;
  let itemId: string | null = null;
  let deviationGroupBy: DeviationGroupBy = null;
  let deviationFilter: DeviationFilter = 'all';
  let deviationAssignee: string | null = null;

  // Parse structured URL paths
  // Examples:
  // /projects/my-space/my-folder/my-list
  // /projects/my-space/my-folder/my-list/tasks/{taskId}
  // /projects/my-space/my-folder/documents/{docId}
  // /deviations/person/{assignee}
  // /inbox, /team, /timesheet, /projects (global views)

  if (pathParts.length === 0) {
  // Root path - default to list view
  viewMode = 'list';
  } else if (pathParts[0] === 'projects' && pathParts.length === 2 && pathParts[1] === 'all') {
  // /projects/all - "Tutti i Task" (all tasks, no path filter)
  path = null;
  const allQueryViewMode = searchParams.get('view') as ViewMode;
  if (allQueryViewMode && VIEW_MODES.includes(allQueryViewMode) && !viewModesWithoutPathFilters.includes(allQueryViewMode)) {
      viewMode = allQueryViewMode;
  } else {
      viewMode = 'list';
  }
  } else if (pathParts[0] === 'projects' && pathParts.length >= 2) {
  // /projects/{path} or /projects/{path}/documents/{itemId} or /projects/{path}/tasks/{taskId}

  // Check if it's an item route
  const itemTypes = ['documents', 'chats', 'voice-channels', 'spreadsheets', 'presentations', 'tasks'];
  const itemTypeIndex = pathParts.findIndex((part, idx) => idx > 0 && itemTypes.includes(part));

  if (itemTypeIndex > 0) {
      // It's an item route: /projects/{path}/{itemType}/{itemId}
      path = pathParts.slice(1, itemTypeIndex).map(decodeURIComponent).join('/');

      const itemTypeStr = pathParts[itemTypeIndex];
      if (itemTypeStr === 'tasks') {
    taskId = pathParts.length > itemTypeIndex + 1 ? decodeURIComponent(pathParts[itemTypeIndex + 1]) : null;
      } else {
    const typeMap: Record<string, ItemType> = {
          'documents': 'document',
          'chats': 'chat',
          'voice-channels': 'voice_channel',
          'spreadsheets': 'spreadsheet',
          'presentations': 'presentation',
    };
    itemType = typeMap[itemTypeStr] || null;
    itemId = pathParts.length > itemTypeIndex + 1 ? decodeURIComponent(pathParts[itemTypeIndex + 1]) : null;
      }
  } else {
      // Regular project route: /projects/{path}
      path = pathParts.slice(1).map(decodeURIComponent).join('/');
  }

  // View mode from query param (default: 'list')
  const queryViewMode = searchParams.get('view') as ViewMode;
  if (queryViewMode && VIEW_MODES.includes(queryViewMode) && !viewModesWithoutPathFilters.includes(queryViewMode)) {
      viewMode = queryViewMode;
  } else {
      viewMode = 'list';
  }
  } else if (pathParts[0] === 'deviations') {
  // /deviations/person/{assignee} or /deviations/task/{taskId}
  viewMode = 'deviations';

  if (pathParts.length >= 3) {
      if (pathParts[1] === 'person') {
    deviationGroupBy = 'person';
    deviationAssignee = decodeURIComponent(pathParts[2]);
      } else if (pathParts[1] === 'task') {
    deviationGroupBy = 'task';
    taskId = decodeURIComponent(pathParts[2]);
      }
  }

  // Parse filter from query
  deviationFilter = (searchParams.get('filter') as DeviationFilter) || 'all';
  if (!deviationGroupBy) {
      deviationGroupBy = (searchParams.get('groupBy') as DeviationGroupBy) || null;
  }
  } else if (viewModesWithoutPathFilters.includes(pathParts[0] as ViewMode)) {
  // Global views: /inbox, /team, /timesheet, /projects
  viewMode = pathParts[0] as ViewMode;
  }

  // Validate itemType
  if (itemType && !['document', 'chat', 'voice_channel', 'spreadsheet', 'presentation'].includes(itemType)) {
  itemType = null;
  }

  // Validate deviationGroupBy
  if (deviationGroupBy && !['person', 'task', 'space', 'list'].includes(deviationGroupBy)) {
  deviationGroupBy = null;
  }

  // Validate deviationFilter
  if (deviationFilter && !['all', 'over', 'under', 'on-time'].includes(deviationFilter)) {
  deviationFilter = 'all';
  }

  return {
  viewMode,
  path,
  taskId,
  itemType,
  itemId,
  deviationGroupBy,
  deviationFilter,
  deviationAssignee,
  };
}

export function buildUrl(state: UrlState): string {
  const parts: string[] = [];
  const params = new URLSearchParams();

  // Build structured URL based on state
  if (state.viewMode === 'deviations') {
  // /deviations/person/{assignee} or /deviations/task/{taskId}
  parts.push('deviations');

  if (state.deviationGroupBy === 'person' && state.deviationAssignee) {
      parts.push('person', encodeURIComponent(state.deviationAssignee));
  } else if (state.deviationGroupBy === 'task' && state.taskId) {
      parts.push('task', encodeURIComponent(state.taskId));
  } else if (state.deviationGroupBy) {
      // Generic grouping
      params.set('groupBy', state.deviationGroupBy);
  }

  if (state.deviationFilter && state.deviationFilter !== 'all') {
      params.set('filter', state.deviationFilter);
  }
  } else if (state.itemType && state.itemId && state.path && !viewModesWithoutPathFilters.includes(state.viewMode)) {
  // /projects/{path}/documents/{docId}
  const typeMap: Record<ItemType, string> = {
      'document': 'documents',
      'chat': 'chats',
      'voice_channel': 'voice-channels',
      'spreadsheet': 'spreadsheets',
      'presentation': 'presentations',
  };
  parts.push('projects', state.path);
  parts.push(typeMap[state.itemType]);
  parts.push(encodeURIComponent(state.itemId));
  } else if (state.taskId && state.path && !viewModesWithoutPathFilters.includes(state.viewMode)) {
  // /projects/{path}/tasks/{taskId}
  parts.push('projects', state.path);
  parts.push('tasks', encodeURIComponent(state.taskId));
  } else if (state.path && !viewModesWithoutPathFilters.includes(state.viewMode)) {
  // /projects/{path} - only if view mode supports path filters
  parts.push('projects', state.path);
  // Add view mode as query param if not default
  if (state.viewMode !== 'list') {
      params.set('view', state.viewMode);
  }
  } else {
  // Views without a path
  if (viewModesWithoutPathFilters.includes(state.viewMode)) {
      // Global views: /inbox, /team, /timesheet, /projects
      parts.push(state.viewMode);
  } else {
      // Project-scoped views without path: /projects/all or /projects/all?view=board
      parts.push('projects', 'all');
      if (state.viewMode !== 'list') {
      params.set('view', state.viewMode);
      }
  }
  }

  // Add deviation params if not in path
  if (state.viewMode !== 'deviations') {
  if (state.deviationGroupBy) params.set('groupBy', state.deviationGroupBy);
  if (state.deviationFilter && state.deviationFilter !== 'all') params.set('filter', state.deviationFilter);
  if (state.deviationAssignee) params.set('assignee', state.deviationAssignee);
  }

  const pathname = '/' + parts.join('/');
  const search = params.toString();
  return search ? `${pathname}?${search}` : pathname;
}

export function getInitialStateFromUrl(): UrlState {
  return parseUrl();
}
